export type GatewayError = {
  message: string
  code?: string
  statusCode?: number
}

export type GatewayResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: GatewayError }

const firstText = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value))
      return String(value)
  }
  return undefined
}

/**
 * The Gateway reports a failure either in its own envelope
 * ({ success: false, message, error, code }) or as a plain HTTP status that
 * never reaches the envelope — a 400 from path binding, a 500 from the
 * database. Read both, and keep the code and the status so support can match
 * the screen against the Gateway log.
 */
export const toGatewayError = (
  error: unknown,
  fallbackMessage: string
): GatewayError => {
  const response = (
    error as { response?: { status?: unknown; data?: unknown } }
  )?.response
  const body = (response?.data ?? {}) as Record<string, unknown>
  const nested = (body.error ?? {}) as Record<string, unknown>

  const status =
    typeof response?.status === 'number' ? response.status : undefined
  const envelopeStatus =
    typeof body.statusCode === 'number' ? body.statusCode : undefined

  return {
    message:
      firstText(
        body.message,
        typeof body.error === 'string' ? body.error : nested.message,
        (error as { message?: unknown })?.message
      ) ?? fallbackMessage,
    code: firstText(body.code, body.errorCode, nested.code),
    statusCode: status ?? envelopeStatus,
  }
}

/** Reads the failure out of an envelope that answered 200 without data. */
export const fromGatewayEnvelope = (
  body: unknown,
  fallbackMessage: string
): GatewayError => toGatewayError({ response: { data: body } }, fallbackMessage)

/**
 * One line a user can act on, or read out to support: the reason first, then
 * whichever identifier the Gateway sent.
 */
export const describeGatewayError = (
  error: GatewayError | null | undefined,
  fallbackMessage: string
): string => {
  const message = error?.message?.trim() || fallbackMessage
  const identifier = error?.code ?? error?.statusCode

  return identifier === undefined || identifier === null
    ? message
    : `${message} (קוד ${identifier})`
}
